import { FunctionComponent, memo, useCallback, useMemo } from 'react';
import { BlockTypes, GalleryItem, useCardTitle } from '../../../shared';

import {
  StoryCard,
  StoryCardArticleContent,
  StoryCardContentArticlePost,
  StoryCardContentArticleQuote,
  StoryCardContentGallery,
  StoryCardContentText,
  StoryCardContentTitle,
  StoryCardPollContent,
  StoryCardPostContentBlockFields,
  StoryCardQuoteContent,
  StoryCardType,
} from '../../../services';
import { DetailsBlock } from '../DetailsBlock';

interface DetailsCardToBlockProps {
  card: StoryCard;
  chatId?: number;
}

export const DetailsCardToBlock: FunctionComponent<DetailsCardToBlockProps> = memo(
  ({ card, chatId }) => {
    const cardTitle = useCardTitle(card);

    const { id: cardId, storyId, type, content, mentionedUsers, event } = card;

    const { title } = content as StoryCardContentTitle;

    const { sourceName, subHeadline, textHTML } = content as StoryCardContentText;

    const { headline, text } = content as StoryCardContentText | StoryCardPollContent;

    const { abstract } = content as StoryCardContentArticlePost;

    const { url } = content as StoryCardContentArticleQuote;

    const { urlFormatted } = content as StoryCardArticleContent;

    const { quote, quoteSource } = content as StoryCardQuoteContent;

    const { gallery } = content as StoryCardContentGallery;

    const getPreviewSrc = useCallback(
      (galleryItem: GalleryItem) => {
        const imageUrl = galleryItem?.image?.url;

        switch (type) {
          case StoryCardType.IMAGE:
          case StoryCardType.QUOTE:
          case StoryCardType.POST:
          case StoryCardType.ARTICLE:
          case StoryCardType.POLL:
            return imageUrl;
          case StoryCardType.AUDIO:
            return imageUrl || galleryItem?.audio?.thumb;
          case StoryCardType.VIDEO:
            return imageUrl || galleryItem?.video?.thumb;
          case StoryCardType.PDF:
            return imageUrl || galleryItem?.pdf?.thumb;
          default:
            return '';
        }
      },
      [type]
    );

    const galleryItems = gallery as GalleryItem[];

    const galleryItem = galleryItems?.[0] as GalleryItem;

    const rightholder = useMemo(
      () => galleryItem?.image?.rightholder,
      [galleryItem?.image?.rightholder]
    );

    const caption = useMemo(() => galleryItem?.title, [galleryItem?.title]);

    const sourceNameBlock = useMemo(() => {
      if (!Boolean(sourceName)) {
        return null;
      }

      return { type: BlockTypes.paragraph, data: { text: sourceName, source: true } };
    }, [sourceName]);

    const headlineBlock = useMemo(() => {
      if (!Boolean(headline || title)) {
        return null;
      }

      return { type: BlockTypes.header, data: { text: headline || title, level: 1 } };
    }, [headline, title]);

    const mentionBlock = useMemo(() => {
      if (!Boolean(mentionedUsers.length)) {
        return null;
      }

      return { type: BlockTypes.mention, data: { mentionedUsers } };
    }, [mentionedUsers]);

    const pollBlock = useMemo(() => {
      return { type: BlockTypes.poll, data: { card } };
    }, [card]);

    const threadBlock = useMemo(() => {
      return { type: BlockTypes.thread, data: { title } };
    }, [title]);

    const subHeadlineBlock = useMemo(() => {
      if (!Boolean(subHeadline)) {
        return null;
      }

      return { type: BlockTypes.header, data: { text: subHeadline, level: 4 } };
    }, [subHeadline]);

    const textBlock = useMemo(() => {
      if (!Boolean(textHTML || text)) {
        return null;
      }

      return { type: BlockTypes.paragraph, data: { text: textHTML || text } };
    }, [text, textHTML]);

    const imageBlock = useMemo(() => {
      if (!Boolean(galleryItems?.length)) {
        return null;
      }

      return {
        type: BlockTypes.image,
        data: {
          items: galleryItems.map((galleryItem: GalleryItem) => {
            return {
              caption: galleryItem.title,
              image: { id: galleryItem.image?.id, url: getPreviewSrc(galleryItem) },
              copyright: galleryItem.image?.rightholder,
            };
          }),
          withBorder: false,
          withBackground: false,
          stretched: false,
        },
      };
    }, [galleryItems, getPreviewSrc]);

    const audioBlock = useMemo(() => {
      const audio = galleryItem?.audio;

      if (!Boolean(audio)) {
        return null;
      }

      return {
        type: BlockTypes.audio,
        data: {
          items: [
            { audio, caption, previewUrl: getPreviewSrc(galleryItem), copyright: rightholder },
          ],
        },
      };
    }, [caption, galleryItem, getPreviewSrc, rightholder]);

    const videoBlock = useMemo(() => {
      const video = galleryItem?.video;

      if (!Boolean(video)) {
        return null;
      }

      return {
        type: BlockTypes.video,
        data: {
          items: [
            { video, caption, copyright: rightholder, previewUrl: getPreviewSrc(galleryItem) },
          ],
        },
      };
    }, [caption, galleryItem, getPreviewSrc, rightholder]);

    const pdfBlock = useMemo(() => {
      const pdf = galleryItem?.pdf;

      if (!Boolean(pdf)) {
        return null;
      }

      return {
        type: BlockTypes.pdf,
        data: {
          items: [{ pdf, caption, previewUrl: getPreviewSrc(galleryItem), copyright: rightholder }],
        },
      };
    }, [caption, galleryItem, getPreviewSrc, rightholder]);

    const commentBlock = useMemo(() => {
      if (!Boolean(headline)) {
        return null;
      }

      return { type: BlockTypes.header, data: { text: headline, level: 5 } };
    }, [headline]);

    const quoteEmbedBlock = useMemo(() => {
      return {
        type: BlockTypes.embedlink,
        data: {
          link: {
            url,
            text: quote,
            quoteSource,
            image: { url: getPreviewSrc(galleryItem) },
          },
        },
      };
    }, [galleryItem, getPreviewSrc, quote, quoteSource, url]);

    const articleEmbedBlock = useMemo(() => {
      return {
        type: BlockTypes.embedlink,
        data: {
          link: {
            url,
            urlFormatted,
            title,
            text: abstract,
            quoteSource,
            image: { url: getPreviewSrc(galleryItem) },
          },
        },
      };
    }, [abstract, galleryItem, getPreviewSrc, quoteSource, title, url, urlFormatted]);

    const eventBlock = useMemo(() => {
      return { type: BlockTypes.event, data: { event } };
    }, [event]);

    const blocks = useMemo(() => {
      switch (type) {
        case StoryCardType.EDITORIAL:
          return [sourceNameBlock, headlineBlock, mentionBlock, subHeadlineBlock, textBlock];
        case StoryCardType.IMAGE:
          return [
            sourceNameBlock,
            headlineBlock,
            mentionBlock,
            imageBlock,
            subHeadlineBlock,
            textBlock,
          ];
        case StoryCardType.PDF:
          return [
            sourceNameBlock,
            headlineBlock,
            mentionBlock,
            pdfBlock,
            subHeadlineBlock,
            textBlock,
          ];
        case StoryCardType.AUDIO:
          return [
            sourceNameBlock,
            headlineBlock,
            mentionBlock,
            audioBlock,
            subHeadlineBlock,
            textBlock,
          ];
        case StoryCardType.VIDEO:
          return [
            sourceNameBlock,
            headlineBlock,
            mentionBlock,
            videoBlock,
            subHeadlineBlock,
            textBlock,
          ];
        case StoryCardType.THREAD:
          return [mentionBlock, threadBlock];
        case StoryCardType.QUOTE:
          return [commentBlock, mentionBlock, quoteEmbedBlock];
        case StoryCardType.ARTICLE:
          return [commentBlock, mentionBlock, articleEmbedBlock];
        case StoryCardType.POLL:
          return [headlineBlock, mentionBlock, videoBlock ?? imageBlock, textBlock, pollBlock];
        case StoryCardType.EVENT:
          return [commentBlock, eventBlock];
        default:
          return null;
      }
    }, [
      articleEmbedBlock,
      audioBlock,
      commentBlock,
      eventBlock,
      headlineBlock,
      imageBlock,
      mentionBlock,
      pdfBlock,
      pollBlock,
      quoteEmbedBlock,
      sourceNameBlock,
      subHeadlineBlock,
      textBlock,
      threadBlock,
      type,
      videoBlock,
    ]) as StoryCardPostContentBlockFields[];

    return (
      <>
        {blocks
          ?.filter((block) => block !== null)
          .map((block, index) => (
            <DetailsBlock
              key={`${block.type}${index}`}
              block={block}
              storyId={storyId}
              cardId={cardId}
              chatId={chatId}
              cardTitle={cardTitle}
            />
          ))}
      </>
    );
  }
);
